export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Notified when a request fails with 401 outside of the auth endpoints, so the
 * app can drop a session that the server no longer accepts (expired token,
 * password change, revoked access) without waiting for a refresh.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function rawRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ?? `Request failed with status ${response.status}`,
    );
  }

  return body as T;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401 && (path === "/auth/me" || !path.startsWith("/auth/"))) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return rawRequest<T>(path, options);
      }
      onUnauthorized?.();
    }
    throw error;
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ── Transfer (import / export) ─────────────────────────────────────────────

/**
 * Upload a local image for an existing item (issue #7). Raw body with the
 * file's own MIME type — that is what the server registers parsers for —
 * and it reuses the same 401/refresh path as every other call.
 *
 * Returns the item's full `imageUrls` so the editor can adopt the server's
 * list instead of guessing: a later Save then submits exactly what is
 * stored, and cannot delete the image we just uploaded.
 */
export async function uploadItemImage(itemId: string, file: File): Promise<{ imageUrls: string[]; version: number }> {
  const doUpload = async () => {
    const response = await fetch(`/api/items/${itemId}/image`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });

    const text = await response.text();
    const body = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(
        response.status,
        error?.code ?? "upload_failed",
        error?.message ?? "Image upload failed.",
      );
    }
    return (body as { data: { imageUrls: string[]; version: number } }).data;
  };

  try {
    return await doUpload();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const refreshed = await attemptRefresh();
      if (refreshed) return await doUpload();
      onUnauthorized?.();
    }
    throw error;
  }
}

export interface ImportSummary {
  parsed: number;
  dropped: number;
  duplicate: number;
  inserted: number;
  insertedIds: string[];
  replaced: number;
  /** Funko detail rows written (sticker / chase / vault / box condition). */
  details: number;
  /** Number of items enriched from gt-poppedia during import. */
  enriched?: number;
  /** Source columns that matched no known alias and were not mapped. */
  unmatchedHeaders: string[];
  errors: Array<{ row: number; message: string }>;
}

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
}

export interface ImportAnalysis {
  headers: string[];
  rowCount: number;
  fields: ImportField[];
  presets: Array<{ id: string; label: string }>;
  /** Suggested field -> column mapping per preset id. */
  mappings: Record<string, Record<string, string>>;
  unmatchedHeaders: string[];
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

/** GET a CSV export and save it, honouring the server-provided filename. */
export async function downloadCsv(path: string, fallbackFilename: string): Promise<void> {
  const response = await fetch(`/api${path}`, { credentials: "include" });

  if (!response.ok) {
    let message = `Export failed with status ${response.status}`;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      if (body.error?.message) message = body.error.message;
    } catch {
      // Response was not JSON; keep the status-based message.
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filenameFromDisposition(
    response.headers.get("content-disposition"),
    fallbackFilename,
  );
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Inspect a CSV without writing: headers, suggested mappings and unmatched columns. */
export async function analyzeImportCsv(file: File): Promise<ImportAnalysis> {
  const content = await file.text();
  const response = await fetch("/api/transfer/import/analyze", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "text/csv" },
    body: content,
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "analyze_failed",
      error?.message ?? "Could not read the CSV.",
    );
  }

  return (body as { data: ImportAnalysis }).data;
}

/** Upload a legacy CSV. Returns the server's summary of what it did. */
export async function importCsv(
  file: File,
  options: {
    mode: "append" | "replace";
    dryRun: boolean;
    duplicates: "skip" | "append";
    enrichFromGtPoppedia?: boolean;
    mapping?: Record<string, string>;
  },
): Promise<ImportSummary> {
  const content = await file.text();
  const params = new URLSearchParams({
    mode: options.mode,
    dryRun: String(options.dryRun),
    duplicates: options.duplicates,
  });
  if (options.enrichFromGtPoppedia) {
    params.set("enrichFromGtPoppedia", "true");
  }

  const response = await fetch(`/api/transfer/import?${params.toString()}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content, mapping: options.mapping ?? {} }),
  });

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(response.status, error?.code ?? "import_failed", error?.message ?? "Import failed");
  }

  return (body as { data: ImportSummary }).data;
}

